import { PrimitiveType } from 'intl-messageformat';
import { isEmpty } from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { create, persist } from 'mobx-persist';

import { DEFAULT_LOCALE, localesMap } from '@constants';
import { LanguageKey } from '@models/Language';
import { queryClient } from '@providers/QueryProvider';
import { QUERY_STRINGS } from '@routes/routes.types';
import { isLocalEnv, isLocalOrTestEnv } from '@utils/featureFlags';
import { isValidLanguage } from '@utils/localize.utils';
import { urlSearchParams } from '@utils/navigation';

import {
  getTranslationQueryData,
  queryTranslations,
} from './localization.queries';
import { formatTranslation } from './localization.store.utils';

export class LocalizationStore {
  @persist language: LanguageKey = DEFAULT_LOCALE.language;

  /** get contentful translations */
  translations = getTranslationQueryData(this.language, queryClient);

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Getter for checking whether Contentful translation content is available and can be displayed */
  get isReady() {
    return this.translations && !isEmpty(this.translations);
  }

  get locale() {
    return localesMap[this.language];
  }

  async fetchTranslations(language: LanguageKey) {
    return await queryTranslations(language, queryClient).then(
      (translations) => {
        // update translations if lanugage matches active selection
        if (language === this.language) {
          runInAction(() => (this.translations = translations));
        }

        return translations;
      }
    );
  }

  setLanguage(language: LanguageKey) {
    this.fetchTranslations(language).then((translations) => {
      runInAction(() => {
        this.translations = translations;
        // update language selection only after localized content is fetched
        this.language = language;
      });
    });
  }

  /**
   * Utility for obtaining a translation by translation Contentful key
   */
  getTranslation(
    contentKey: string,
    formatProps?: Record<string, PrimitiveType>,
    ignoreNonExistent = !isLocalOrTestEnv
  ) {
    const translation =
      this.translations?.[contentKey] ??
      (!ignoreNonExistent ? `🚨 Missing translation for ${contentKey} 🚨` : '');

    if (formatProps) {
      return formatTranslation(translation, formatProps, this.language);
    }

    return translation;
  }
}

export const localizationStore = new LocalizationStore();

create({})('omasato-loc', localizationStore).then((store) => {
  const paramLang = urlSearchParams.get(QUERY_STRINGS.LANGUAGE);

  // override persisted language with URL param
  if (isValidLanguage(paramLang) && paramLang !== localizationStore.language) {
    // NOTE: triggers refreshing translations as well
    localizationStore.setLanguage(paramLang);
  } else {
    // fetch translations after rehydrating persisted language selection
    localizationStore.fetchTranslations(store.language);
  }

  if (isLocalEnv) console.info('[LocalizationStore] inited', store);
});

/** Alias for `localizationStore.getTranslation` for more concise function invocation */
export const t = localizationStore.getTranslation;

export type TranslatorFn = typeof t;
